import { MapStyle } from 'appConstants';
import { ColorScheme, LegendType } from 'appTypes';
import Color from 'colorjs.io';
import { LegendColorSchemes } from 'features/workflow_timeline/metricsOptions';

import { null_grey } from '../../theme/cemTheme';
import { arrayEquals } from '../common/utils';

export function format_color_for_mapbox(col: Color) {
  const fmt_255 = (v) => (v * 255).toFixed(0);
  const rgb_bit = `${fmt_255(col.srgb.r)},${fmt_255(col.srgb.g)},${fmt_255(
    col.srgb.b,
  )}`;
  if (col.alpha === 1) {
    const text = `rgb(${rgb_bit})`;
    return text;
  } else {
    const text = `rgba(${rgb_bit},${col.alpha})`;
    return text;
  }
}

export class ColorGradient {
  stop_colors: Array<{ stop: number; color: Color }>;
  constructor(stopsColors) {
    this.stop_colors = [];
    const colors = [];
    const stops = [];

    for (let i = 0; i < stopsColors.length; i++) {
      if (i % 2) {
        const new_col = new Color(stopsColors[i]);
        colors.push(new_col);
      } else {
        stops.push(stopsColors[i]);
      }
    }

    for (let i = 0; i < stops.length; i++) {
      this.stop_colors.push({ stop: stops[i], color: colors[i] });
    }

    // Stops are out of order, sort them
    if (!arrayEquals([...stops].sort(), stops)) {
      this.stop_colors = this.stop_colors.sort((a, b) => a.stop - b.stop);
    }
  }

  reverse() {
    this.stop_colors = this.stop_colors.reverse();
    return this;
  }

  toInterleaved() {
    const ret = [];
    for (let i = 0; i < this.stop_colors.length; i++) {
      ret.push(this.stop_colors[i].stop);
      const { color } = this.stop_colors[i];
      ret.push(format_color_for_mapbox(color));
    }
    return ret;
  }

  sample(value: number) {
    let sampling = -1;
    for (let i = 0; i < this.stop_colors.length; i++) {
      if (value <= this.stop_colors[i].stop) {
        sampling = i;
        break;
      }
    }
    if (sampling <= 0) {
      return this.stop_colors[0].color;
    } else if (sampling >= this.stop_colors.length - 1 || sampling === -1) {
      return this.stop_colors[this.stop_colors.length - 1].color;
    } else {
      const start = this.stop_colors[sampling - 1];
      const end = this.stop_colors[sampling];
      const relativeMix = (value - start.stop) / (end.stop - start.stop);
      console.assert(relativeMix > 0 && relativeMix <= 1);
      return start.color.range(end.color)(relativeMix);
    }
  }

  stops(): number[] {
    const ret: number[] = [];
    for (let i = 0; i < this.stop_colors.length; i++) {
      ret.push(this.stop_colors[i].stop);
    }
    return ret;
  }

  colors_hex(): string[] {
    const ret: string[] = [];
    for (let i = 0; i < this.stop_colors.length; i++) {
      ret.push(this.stop_colors[i].color.toString({ format: 'hex' }));
    }
    return ret;
  }
}

export function viridis(lowStop, highStop) {
  const diff = (highStop - lowStop) / 4;
  return new ColorGradient([
    lowStop,
    'rgb(68, 1, 84)',
    lowStop + diff,
    'rgb(59, 82, 139)',
    lowStop + diff * 2,
    'rgb(33, 145, 140)',
    lowStop + diff * 3,
    'rgb(94, 201, 98)',
    highStop,
    'rgb(253, 231, 37)',
  ]).toInterleaved();
}

export function grey_yellow_red(greyStop, redStop) {
  const midStop = (greyStop + redStop) / 2;
  const yellow = 'rgb(207,183,98)';
  const red = 'rgb(253,61,60)';

  return new ColorGradient([
    greyStop,
    null_grey,
    midStop,
    yellow,
    redStop,
    red,
  ]);
}

export function timeline_bar_gradient(greyStop, redStop) {
  const midStop = (greyStop + redStop) / 3;
  const midStop2 = (2 * (greyStop + redStop)) / 3;
  const mid = 'rgb(180,151,231)';
  return new ColorGradient([
    greyStop,
    null_grey,
    midStop,
    null_grey,
    midStop2,
    mid,
    redStop,
    mid,
  ]);
}

const MAP_STYLE_COLORS = {
  [MapStyle.Dark]: {
    red: '#FD4443',
    orange: '#FD9A54',
    yellow: '#FBDD4B',
    light_green: '#889F3C',
    green: '#29BF54',
    blue: '#1379F6',
    light_neutral: 'rgba(255,240,139,0.5)',
    neutral: 'rgba(0,0,0,0)',
  },
  [MapStyle.Light]: {
    red: '#FD3D3C',
    orange: '#FD8C38',
    yellow: '#FCD32E',
    light_green: '#889F3C',
    green: '#1B7E41',
    blue: '#1379F6',
    light_neutral: 'rgba(255,240,139,0.5)',
    neutral: 'rgba(207,207,207,0.5)',
  },
  [MapStyle.Sat]: {
    red: '#FD3D3C',
    orange: '#FD8C38',
    yellow: '#FCD32E',
    light_green: '#889F3C',
    green: '#1B7E41',
    blue: '#1379F6',
    light_neutral: 'rgba(255,240,139,0.5)',
    neutral: 'rgba(0,0,0,0)',
  },
};

const CONGESTION_COLORS = {
  red: '#DF5E33',
  orange: '#E99047',
  light_orange: '#EEAB53',
  yellow: '#F4C75F',
  light_yellow: '#FAEC82',
  green: '#306b35',
  text_green: '#458B4B',
  blue: '#3877AB',
  text_blue: '#4E8DC2',
};

function getStyleColors(mapStyle: MapStyle) {
  return MAP_STYLE_COLORS[mapStyle] ?? MAP_STYLE_COLORS.dark;
}

export function buildTrafficLightColors(
  mapStyle: MapStyle,
  colorScheme: ColorScheme,
  legendType: LegendType,
  range: '0-1' | '100-0' = '0-1',
) {
  const styleColors = getStyleColors(mapStyle);
  const endColor =
    colorScheme === 'green' ? styleColors.green : styleColors.neutral;
  const secondToEndColor =
    colorScheme === 'green'
      ? styleColors.light_green
      : styleColors.light_neutral;

  if (legendType === 'continuous') {
    // Continuous mode
    const lowStop = range === '0-1' ? 1.0 : 100;
    const highStop = range === '0-1' ? 0.0 : 0;
    const midStop = (2.0 / 3) * lowStop + (1.0 / 3) * highStop;

    return new ColorGradient([
      lowStop,
      endColor,
      midStop,
      styleColors.yellow,
      highStop,
      styleColors.red,
    ]).toInterleaved();
  } else {
    // Discrete mode
    if (range === '100-0') {
      // Delay metric: 0-100 range
      return [
        0,
        styleColors.red,
        20,
        styleColors.orange,
        40,
        styleColors.yellow,
        60,
        secondToEndColor,
        80,
        endColor,
        100,
        endColor,
      ];
    } else {
      // Speed metric: 0-1 range
      return [
        0,
        styleColors.red,
        0.2,
        styleColors.orange,
        0.4,
        styleColors.yellow,
        0.6,
        secondToEndColor,
        0.8,
        endColor,
        1,
        endColor,
      ];
    }
  }
}

export function buildVolumeColors(
  mapStyle: MapStyle,
  colorScheme: ColorScheme,
  legendType: LegendType,
) {
  const styleColors = getStyleColors(mapStyle);
  const endColor =
    colorScheme === 'green' ? styleColors.green : styleColors.neutral;

  if (legendType === 'continuous') {
    return new ColorGradient([
      -2,
      styleColors.red,
      -1,
      styleColors.orange,
      -0.5,
      styleColors.yellow,
      0,
      endColor,
      1,
      styleColors.blue,
    ]).toInterleaved();
  } else {
    // Discrete mode
    return [
      -2,
      styleColors.red,
      -1,
      styleColors.orange,
      -0.5,
      styleColors.yellow,
      -0.25,
      endColor,
      0.25,
      styleColors.blue,
      1,
      styleColors.blue,
    ];
  }
}

export function buildDetourVolumeColors(
  mapStyle: MapStyle,
  colorScheme: ColorScheme,
  legendType: LegendType,
) {
  const styleColors = getStyleColors(mapStyle);
  const endColor =
    colorScheme === 'green' ? styleColors.green : styleColors.neutral;

  if (legendType === 'continuous') {
    return new ColorGradient([
      -2,
      styleColors.red,
      -1.5,
      styleColors.orange,
      -1,
      styleColors.yellow,
      0,
      endColor,
      1,
      styleColors.blue,
    ]).toInterleaved();
  } else {
    // Discrete mode
    return [
      -2,
      styleColors.red,
      -1.5,
      styleColors.orange,
      -1,
      styleColors.yellow,
      -0.25,
      endColor,
      0.25,
      styleColors.blue,
      1,
      styleColors.blue,
    ];
  }
}

export function buildDetourAtypicalVolumeColors(
  mapStyle: MapStyle,
  colorScheme: ColorScheme,
  legendType: LegendType,
) {
  const styleColors = getStyleColors(mapStyle);
  const endColor =
    colorScheme === 'green' ? styleColors.green : styleColors.neutral;

  if (legendType === 'continuous') {
    return new ColorGradient([
      -2,
      styleColors.red,
      -1,
      styleColors.orange,
      -0.5,
      styleColors.yellow,
      0,
      endColor,
      1,
      styleColors.blue,
    ]).toInterleaved();
  } else {
    // Discrete mode
    return [
      -2,
      styleColors.red,
      -1,
      styleColors.orange,
      -0.5,
      styleColors.yellow,
      -0.25,
      endColor,
      0.25,
      styleColors.blue,
      1,
      styleColors.blue,
    ];
  }
}

function buildCongestionColors(colorScheme: ColorScheme) {
  const endColor =
    colorScheme === 'green' ? CONGESTION_COLORS.green : CONGESTION_COLORS.blue;
  return [
    0.0,
    CONGESTION_COLORS.red, // 100% - most congested
    0.1,
    CONGESTION_COLORS.orange,
    0.25,
    CONGESTION_COLORS.light_orange,
    0.4,
    CONGESTION_COLORS.yellow,
    0.5,
    CONGESTION_COLORS.light_yellow,
    0.7,
    CONGESTION_COLORS.light_yellow,
    1.0,
    endColor, // 0% - freeflow color - used for gradient in the last element
  ];
}

export function getMetricColorSchemes(
  metricName: string,
  mapStyle?: MapStyle, // mapStyle doesn't influnece the colors in congestion metrics
  discreteOnly: boolean = false, // congestion metric has only discrete mode
): LegendColorSchemes {
  const discrete = {
    discrete: {
      neutral: getMetricColorScheme(
        mapStyle,
        'discrete',
        'neutral',
        metricName,
      ),
      green: getMetricColorScheme(mapStyle, 'discrete', 'green', metricName),
    },
  };
  if (discreteOnly) return discrete;

  const continuous = {
    continuous: {
      neutral: getMetricColorScheme(
        mapStyle,
        'continuous',
        'neutral',
        metricName,
      ),
      green: getMetricColorScheme(mapStyle, 'continuous', 'green', metricName),
    },
  };

  return { ...continuous, ...discrete };
}

function getMetricColorScheme(
  mapStyle: MapStyle,
  legendType: LegendType,
  colorScheme: ColorScheme,
  metricName: string,
) {
  if (metricName === '0_1_stops') {
    return buildTrafficLightColors(mapStyle, colorScheme, legendType, '0-1');
  } else if (metricName === '100_0_stops') {
    return buildTrafficLightColors(mapStyle, colorScheme, legendType, '100-0');
  } else if (metricName === 'volume_stops') {
    return buildVolumeColors(mapStyle, colorScheme, legendType);
  } else if (metricName === 'detour_volume_stops') {
    return buildDetourVolumeColors(mapStyle, colorScheme, legendType);
  } else if (metricName === 'detour_atypical_volume_stops') {
    return buildDetourAtypicalVolumeColors(mapStyle, colorScheme, legendType);
  } else if (metricName === 'congestion') {
    return buildCongestionColors(colorScheme);
  }
}

function makeCongestionColorRampStops(freeflowColor: string) {
  return [
    0.0,
    CONGESTION_COLORS.red,
    0.0999,
    CONGESTION_COLORS.red,
    0.1,
    CONGESTION_COLORS.orange,
    0.2499,
    CONGESTION_COLORS.orange,
    0.25,
    CONGESTION_COLORS.light_orange,
    0.399,
    CONGESTION_COLORS.light_orange,
    0.4,
    CONGESTION_COLORS.yellow,
    0.499,
    CONGESTION_COLORS.yellow,
    0.5,
    CONGESTION_COLORS.light_yellow,
    0.7,
    CONGESTION_COLORS.light_yellow,
    0.95,
    freeflowColor,
    1,
    freeflowColor,
  ];
}

export const COLORMAP_QUEUE_CONGESTION = new ColorGradient(
  makeCongestionColorRampStops(CONGESTION_COLORS.green),
);

export const COLORMAP_QUEUE_CONGESTION_BLUE = new ColorGradient(
  makeCongestionColorRampStops(CONGESTION_COLORS.blue),
);

export const COLORMAP_QUEUE_CONGESTION_FOR_TEXT = new ColorGradient(
  makeCongestionColorRampStops(CONGESTION_COLORS.text_green),
);

export const COLORMAP_QUEUE_CONGESTION_BLUE_FOR_TEXT = new ColorGradient(
  makeCongestionColorRampStops(CONGESTION_COLORS.text_blue),
);
